import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { Add, Edit, Delete, Discount } from '@mui/icons-material';
import { ModuleLayout } from '../../components/Layout/ModuleLayout';
import PendingApprovalsSection from '../../components/common/PendingApprovalsSection';
import apiClient from '../../api/client';
import { moduleService, moduleDiscountService, ModuleDiscount } from '../../services';
import { usePermission } from '../../hooks';
import { colors } from '../../theme/theme';

export const ModuleDiscountsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { can } = usePermission();
  const [module, setModule] = useState<any>(null);
  const [discounts, setDiscounts] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    service_id: '',
    type: '',
    value: '',
    start_date: '',
    end_date: '',
    is_active: true,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [tabValue, setTabValue] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [moduleRes, discountsRes, servicesRes] = await Promise.all([
        moduleService.getModule(Number(id)),
        moduleDiscountService.getAll(Number(id!)),
        apiClient.get(`/admin/county/modules/${id}/services`),
      ]);

      if (moduleRes.status === 'success') {
        setModule(moduleRes.data);
      }
      if (discountsRes.status === 'success' && discountsRes.data) {
        const raw = discountsRes.data as any;
        const discountsData = Array.isArray(raw) ? raw : (raw?.data ?? []);
        setDiscounts(Array.isArray(discountsData) ? discountsData : []);
      }
      if (servicesRes.status === 'success') {
        const servicesData = servicesRes.data?.data || servicesRes.data || [];
        setServices(Array.isArray(servicesData) ? servicesData : []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (discount?: any) => {
    if (discount) {
      setSelectedDiscount(discount);
      setFormData({
        name: discount.name || '',
        description: discount.description || '',
        service_id: discount.service_id?.toString() || '',
        type: discount.type?.toString() || '',
        value: discount.value?.toString() || '',
        start_date: discount.start_date ? discount.start_date.split('T')[0] : '',
        end_date: discount.end_date ? discount.end_date.split('T')[0] : '',
        is_active: discount.is_active ?? true,
      });
    } else {
      setSelectedDiscount(null);
      setFormData({
        name: '',
        description: '',
        service_id: '',
        type: '',
        value: '',
        start_date: '',
        end_date: '',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDiscount(null);
    setFormData({
      name: '',
      description: '',
      service_id: '',
      type: '',
      value: '',
      start_date: '',
      end_date: '',
      is_active: true
    });
  };

  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (!formData.service_id || !formData.type || !formData.name || !formData.value) {
        setSnackbar({
          open: true,
          message: 'Please fill in all required fields',
          severity: 'error',
        });
        return;
      }

      const data = {
        name: formData.name,
        description: formData.description || undefined,
        service_id: parseInt(formData.service_id),
        type: parseInt(formData.type),
        value: parseFloat(formData.value),
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        is_active: formData.is_active,
      };

      let response;
      if (selectedDiscount) {
        response = await moduleDiscountService.update(Number(id!), selectedDiscount.id, data);
        if (response.status === 'success') {
          setSnackbar({
            open: true,
            message: response.message || 'Update request submitted for approval',
            severity: 'success',
          });
          setTabValue(1);
        }
      } else {
        response = await moduleDiscountService.create(Number(id!), data);
        if (response.status === 'success') {
          setSnackbar({
            open: true,
            message: 'Discount created successfully',
            severity: 'success',
          });
        }
      }
      fetchData();
      handleCloseDialog();
    } catch (err: any) {
      console.error('Failed to save discount:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.errors || 'Failed to save discount';
      setSnackbar({
        open: true,
        message: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage),
        severity: 'error',
      });
    }
  };

  const handleDelete = async (discountId: number) => {
    if (!window.confirm('Are you sure you want to submit a delete request for this discount?')) return;
    try {
      const response = await moduleDiscountService.delete(Number(id!), discountId);
      if (response.status === 'success') {
        setSnackbar({
          open: true,
          message: response.message || 'Delete request submitted for approval',
          severity: 'success',
        });
        setTabValue(1);
        fetchData();
      }
    } catch (err: any) {
      console.error('Failed to delete discount:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to delete discount',
        severity: 'error',
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getTypeLabel = (type: number) => {
    return type === 1 ? 'Fixed' : type === 2 ? 'Percentage' : 'N/A';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatValue = (type: number, value: number) => {
    if (type === 1) {
      return formatCurrency(value);
    } else if (type === 2) {
      return `${value}%`;
    }
    return value.toString();
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      flex: 1,
      display: 'flex',
    },
    {
      field: 'service',
      headerName: 'Service',
      width: 200,
      flex: 1,
      display: 'flex',
      valueGetter: (_, row) => row.service?.name || 'N/A',
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 150,
      display: 'flex',
      renderCell: (params) => (
        <Chip
          label={getTypeLabel(params.value)}
          size="small"
          sx={{ fontWeight: 600, bgcolor: '#fef3c7', color: '#92400e' }}
        />
      ),
    },
    {
      field: 'value',
      headerName: 'Value',
      width: 150,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
          {formatValue(params.row.type, params.value)}
        </Typography>
      ),
    },
    {
      field: 'start_date',
      headerName: 'Start Date',
      width: 150,
      display: 'flex',
      valueGetter: (_, row) => formatDate(row.start_date),
    },
    {
      field: 'end_date',
      headerName: 'End Date',
      width: 150,
      display: 'flex',
      valueGetter: (_, row) => formatDate(row.end_date),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      display: 'flex',
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      display: 'flex',
      renderCell: (params) => (
        <Box>
          {can('discounts.edit') && (
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
              sx={{ color: colors.primary.main }}
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
          {can('discounts.delete') && (
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ color: '#dc2626' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <ModuleLayout module={module} moduleId={id!}>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout module={module} moduleId={id!}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
              Discounts
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Manage service discounts and promotions
            </Typography>
          </Box>
          {can('discounts.create') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Discount
            </Button>
          )}
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Discounts" />
          <Tab label="Approvals" />
        </Tabs>

        {tabValue === 0 && (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <DataGrid
                  rows={discounts}
                  columns={columns}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[5, 10, 25, 50]}
                  disableRowSelectionOnClick
                  autoHeight
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: '#f9fafb',
                      borderRadius: 0,
                    },
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: colors.primary[50],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 3,
                          }}
                        >
                          <Discount sx={{ fontSize: 40, color: colors.primary.main }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          No Discounts Found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          Create your first discount
                        </Typography>
                        {can('discounts.create') && (
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => handleOpenDialog()}
                            sx={{
                              background: colors.gradients.primary,
                            }}
                          >
                            Add Discount
                          </Button>
                        )}
                      </Box>
                    ),
                  }}
                />
          </CardContent>
        </Card>
          </>
        )}

        {tabValue === 1 && (
          <PendingApprovalsSection
            entityType="discount"
            title="Pending Discount Approvals"
            onApprovalComplete={fetchData}
          />
        )}

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedDiscount ? 'Edit Discount' : 'Add Discount'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Service</InputLabel>
                <Select
                  value={formData.service_id}
                  label="Service"
                  onChange={(e) => setFormData({ ...formData, service_id: e.target.value as string })}
                >
                  <MenuItem value="">
                    <em>Select Service</em>
                  </MenuItem>
                  {services.map((service) => {
                    const hasDiscount = discounts.some(
                      (d) => d.service_id === service.id && (!selectedDiscount || selectedDiscount.service_id !== service.id)
                    );
                    return (
                      <MenuItem key={service.id} value={service.id.toString()} disabled={hasDiscount}>
                        {service.name}{hasDiscount ? ' (discount exists)' : ''}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as string })}
                >
                  <MenuItem value="">
                    <em>Select Type</em>
                  </MenuItem>
                  <MenuItem value="1">Fixed Amount</MenuItem>
                  <MenuItem value="2">Percentage</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                required
                label={formData.type === '2' ? 'Value (%)' : 'Value (KES)'}
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                InputProps={{
                  inputProps: { min: 0, step: formData.type === '2' ? 0.01 : 1, max: formData.type === '2' ? 100 : undefined }
                }}
              />
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                background: colors.gradients.primary,
              }}
            >
              {selectedDiscount ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ModuleLayout>
  );
};
